import React, { useState } from "react";
import { motion } from "framer-motion";
import { useInView } from "framer-motion";
import { useRef } from "react";
import { Github, ExternalLink, Calendar } from "lucide-react";
import "./Projects.css";
import ProjectModal from "../Utility/ProjectModal";

const Projects = ({ data }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.2 });
  const [filter, setFilter] = useState("all");
  const [selectedProject, setSelectedProject] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const validProjects = data.projects.filter((project) => project !== null);

  const categories = ["all", "web", "ml", "algorithms"];

  const getProjectCategory = (project) => {
    const title = project.title.toLowerCase();
    const body = project.body.toLowerCase();

    if (
      title.includes("web") ||
      title.includes("react") ||
      title.includes("github") ||
      title.includes("todo") ||
      title.includes("e-commerce")
    ) {
      return "web";
    }
    if (
      title.includes("classifier") ||
      title.includes("regression") ||
      title.includes("vehicle") ||
      body.includes("machine learning") ||
      body.includes("ml5")
    ) {
      return "ml";
    }
    if (
      title.includes("algorithm") ||
      title.includes("maze") ||
      title.includes("star")
    ) {
      return "algorithms";
    }
    return "web";
  };

  const filteredProjects =
    filter === "all"
      ? validProjects
      : validProjects.filter(
          (project) => getProjectCategory(project) === filter
        );

  return (
    <section className="projects" id="projects" ref={ref}>
      <div className="container">
        {isModalOpen && (
          <ProjectModal 
            isModalOpen={isModalOpen} 
            setIsModalOpen={setIsModalOpen} 
            project={selectedProject}
          />
        )}
        <motion.div
          className="projects-content"
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.div className="section-header" variants={itemVariants}>
            <h2 className="section-title">Featured Projects</h2>
            <div className="section-line"></div>
          </motion.div>

          <motion.div className="project-filters" variants={itemVariants}>
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-btn ${filter === category ? "active" : ""}`}
                onClick={() => setFilter(category)}
              >
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </button>
            ))}
          </motion.div>

          <motion.div className="projects-grid" variants={containerVariants}>
            {filteredProjects.map((project, index) => (
              <motion.div
                key={index}
                className="project-card"
                variants={itemVariants}
                whileHover={{ y: -10 }}
                layout
              >
                <div className="project-image">
                  <img src={project.img} alt={project.title} />
                  <div className="project-overlay">
                    <div className="project-links">
                      {project.github && (
                        <motion.a
                          href={project.github}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <Github size={20} />
                        </motion.a>
                      )}
                      {project.live && (
                        <motion.a
                          href={project.live}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-link"
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <ExternalLink size={20} />
                        </motion.a>
                      )}
                    </div>
                  </div>
                </div>

                <div className="project-content">
                  <div className="project-meta">
                    <span className="project-category">
                      {getProjectCategory(project)}
                    </span>
                    <div className="project-date">
                      <Calendar size={14} />
                      <span>{project.timePeriod}</span>
                    </div>
                  </div>

                  <h3 className="project-title">{project.title}</h3>

                  <p className="project-description">
                    {project.body.substring(0, 150)}...
                  </p>

                  <div className="project-actions">
                    <motion.button
                      className="btn-learn-more"
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={() => {
                        setSelectedProject(project);
                        setIsModalOpen(true);
                      }}
                    >
                      Learn More
                    </motion.button>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;
